/**
 * How big a QR code is drawn.
 *
 * `react-native-qrcode-styled` sizes a code by its **modules** (`pieceSize`),
 * not by the box the code sits in, and it adds the quiet zone by *enlarging the
 * SVG* rather than the pattern alone: the box a caller gets is
 * `pieceSize × modules + 2 × padding`. A caller that asks for a `size` therefore
 * gets a picture of some other size — the classroom's invitation asked for 200px
 * and was drawn at `8 × 61 + 40 = 528` units, a code whose modules were big
 * enough to look soft and whose centre mark was a hole punched in an already
 * dense pattern.
 *
 * These two functions are the arithmetic that puts the caller back in charge: a
 * module is `size ÷ (modules + quiet zone × 2)`, the quiet zone is the standard's
 * four modules of margin, and the drawn box is therefore exactly `size`.
 *
 * `modules` is the count the library reports for a payload (`onChangeSize`) —
 * the same count `size` depends on, which is why the QR is not drawn until it is
 * known.
 */

/** The margin a scanner needs around a code, in modules: four is the minimum. */
export const QR_QUIET_ZONE_MODULES = 4

/** The drawn size of one module, so that the whole code fits `size` exactly. */
export function qrModuleSize(
  modules: number,
  size: number,
  quietZone = QR_QUIET_ZONE_MODULES,
): number {
  if (!(modules > 0) || !(size > 0)) return 0
  return size / (modules + quietZone * 2)
}

/**
 * The box the drawn code comes out at: the pattern plus its margin, which is
 * `size` when both sides of the contract are honoured.
 */
export function qrDrawnSize(
  modules: number,
  size: number,
  quietZone = QR_QUIET_ZONE_MODULES,
): number {
  const module = qrModuleSize(modules, size, quietZone)
  return module * modules + 2 * quietZone * module
}
